/**
 * Hikvision AES Decryption Utility
 *
 * Hikvision devices use AES encryption when security=1 parameter is used in API requests.
 * This utility provides decryption functions to decode encrypted responses.
 *
 * Algorithm: AES-128-CBC or AES-256-CBC (device dependent)
 * Key: Derived from device password
 * IV: Random 32-character hex string (provided in request)
 *
 * Reference: Hikvision HTTP API documentation section on "Encrypted Communication"
 */

import * as CryptoJS from 'crypto-js';
import { logger } from '../services/logger';

/**
 * Derive AES key from Hikvision device password
 *
 * Hikvision uses MD5 hash of password as the AES encryption key
 *
 * @param password Device password
 * @param method Key derivation method to try
 * @returns CryptoJS WordArray representing the key
 */
const deriveKeyFromPassword = (
  password: string,
  method: 'md5' | 'sha256' | 'direct' = 'md5'
): CryptoJS.lib.WordArray => {
  switch (method) {
    case 'md5':
      // Standard Hikvision: MD5(password)
      return CryptoJS.MD5(password);
    case 'sha256':
      // Alternative: SHA-256(password)
      return CryptoJS.SHA256(password);
    case 'direct':
      // Alternative: Use password directly as UTF-8
      return CryptoJS.enc.Utf8.parse(password);
    default:
      return CryptoJS.MD5(password);
  }
};

/**
 * Decrypt Hikvision encrypted string using AES (internal helper)
 *
 * @param encryptedHex 96-character hex string (encrypted data)
 * @param iv 32-character hex string (initialization vector)
 * @param password Device password (used to derive key)
 * @param keyMethod Key derivation method
 * @returns Decrypted plaintext string, or null if decryption fails
 */
const decryptWithMethod = (
  encryptedHex: string,
  iv: string,
  password: string,
  keyMethod: 'md5' | 'sha256' | 'direct'
): string | null => {
  try {
    // Derive AES key from password
    const key = deriveKeyFromPassword(password, keyMethod);

    // Parse hex strings to CryptoJS format
    const ivWords = CryptoJS.enc.Hex.parse(iv);
    const ciphertext = CryptoJS.enc.Hex.parse(encryptedHex);

    // Decrypt using AES-CBC
    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext } as any,
      key,
      {
        iv: ivWords,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    // Convert to UTF-8 string
    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

    // Verify it's a valid hash (32 or 64 hex characters)
    if (decryptedText && /^[0-9a-f]{32,64}$/i.test(decryptedText)) {
      return decryptedText;
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Decrypt Hikvision encrypted string using AES with preferred method
 *
 * If preferredMethod is provided, tries it first before falling back to other methods.
 * This significantly improves performance when the decryption method is known.
 *
 * @param encryptedHex 96-character hex string (encrypted data)
 * @param iv 32-character hex string (initialization vector)
 * @param password Device password (used to derive key)
 * @param preferredMethod Known working method to try first (optional)
 * @returns Object with decrypted text and the successful method, or null if all fail
 */
export const decryptHikvisionDataWithMethod = (
  encryptedHex: string,
  iv: string,
  password: string,
  preferredMethod?: 'md5' | 'sha256' | 'direct'
): { decrypted: string; method: 'md5' | 'sha256' | 'direct' } | null => {
  try {
    // Validate inputs
    if (!encryptedHex || !iv || !password) {
      return null;
    }

    if (encryptedHex.length !== 96 || iv.length !== 32) {
      return null;
    }

    // Try preferred method first if provided
    if (preferredMethod) {
      const decryptedText = decryptWithMethod(encryptedHex, iv, password, preferredMethod);
      if (decryptedText) {
        return { decrypted: decryptedText, method: preferredMethod };
      }
    }

    // Try all methods
    const methods: Array<'md5' | 'sha256' | 'direct'> = ['md5', 'sha256', 'direct'];

    for (const method of methods) {
      if (method === preferredMethod) continue; // Skip already tried method

      const decryptedText = decryptWithMethod(encryptedHex, iv, password, method);
      if (decryptedText) {
        return { decrypted: decryptedText, method };
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Decrypt Hikvision encrypted string using AES
 *
 * Tries multiple key derivation methods:
 * 1. MD5(password) - Standard Hikvision
 * 2. SHA-256(password) - Alternative
 * 3. Direct password - Fallback
 *
 * Used for decrypting:
 * - employeeNoString (person hash) in AcsEvent responses
 * - name fields in encrypted responses
 * - Other encrypted data fields
 *
 * @param encryptedHex 96-character hex string (encrypted data)
 * @param iv 32-character hex string (initialization vector)
 * @param password Device password (used to derive key)
 * @returns Decrypted plaintext string, or null if decryption fails
 */
export const decryptHikvisionData = (
  encryptedHex: string,
  iv: string,
  password: string
): string | null => {
  try {
    // Validate inputs
    if (!encryptedHex || !iv || !password) {
      logger.warn('hikvision-decrypt', 'Missing required parameters', {
        hasEncrypted: !!encryptedHex,
        hasIV: !!iv,
        hasPassword: !!password,
      });
      return null;
    }

    // Encrypted data should be 96 chars (48 bytes = 384 bits)
    if (encryptedHex.length !== 96) {
      logger.warn('hikvision-decrypt', 'Invalid encrypted data length', {
        length: encryptedHex.length,
        expected: 96,
        encrypted: encryptedHex,
      });
      return null;
    }

    // IV should be 32 chars (16 bytes = 128 bits)
    if (iv.length !== 32) {
      logger.warn('hikvision-decrypt', 'Invalid IV length', {
        length: iv.length,
        expected: 32,
        iv,
      });
      return null;
    }

    // Try multiple key derivation methods
    const methods: Array<'md5' | 'sha256' | 'direct'> = ['md5', 'sha256', 'direct'];

    logger.info('hikvision-decrypt', 'Attempting decryption with multiple methods', {
      encryptedPreview: encryptedHex.substring(0, 20) + '...',
      ivPreview: iv.substring(0, 16) + '...',
      passwordLength: password.length,
      methodsToTry: methods,
    });

    for (const method of methods) {
      const decryptedText = decryptWithMethod(encryptedHex, iv, password, method);

      if (decryptedText) {
        logger.info('hikvision-decrypt', ` Decryption successful with ${method}!`, {
          method,
          encryptedLength: encryptedHex.length,
          decryptedLength: decryptedText.length,
          decrypted: decryptedText,
        });
        return decryptedText;
      } else {
        logger.debug('hikvision-decrypt', ` Method ${method} failed, trying next...`, {
          method,
        });
      }
    }

    // All methods failed
    logger.warn('hikvision-decrypt', 'All decryption methods failed', {
      encryptedHex: encryptedHex.substring(0, 40) + '...',
      iv: iv.substring(0, 16) + '...',
      passwordUsed: '***' + password.substring(Math.max(0, password.length - 4)),
      methodsTried: methods,
    });
    return null;
  } catch (error) {
    logger.error('hikvision-decrypt', 'Decryption failed with exception', {
      error: error instanceof Error ? error.message : String(error),
      encryptedLength: encryptedHex?.length,
      ivLength: iv?.length,
      stack: error instanceof Error ? error.stack : undefined,
    });
    return null;
  }
};

/**
 * Check if a string is likely to be Hikvision encrypted data
 *
 * Encrypted strings are:
 * - 96 characters long (SHA-384 length but actually AES-encrypted)
 * - Hex encoded
 *
 * @param value String to check
 * @returns true if likely encrypted, false otherwise
 */
export const isLikelyEncrypted = (value: string | undefined): boolean => {
  if (!value) return false;

  // Encrypted data is 96 hex characters
  if (value.length !== 96) return false;

  // Check if all characters are valid hex
  return /^[0-9a-f]{96}$/i.test(value);
};

/**
 * Decrypt person hash from Hikvision AcsEvent
 *
 * When security=1 is used, the employeeNoString field contains an encrypted hash
 * instead of the original MD5. This function decrypts it back to the MD5.
 *
 * @param event AcsEvent object from device
 * @param iv Initialization vector used in the request
 * @param password Device password
 * @returns Decrypted MD5 hash, or original hash if not encrypted
 */
export const decryptPersonHashFromEvent = (
  event: any,
  iv: string,
  password: string
): string | null => {
  const employeeNoString = event?.employeeNoString;

  if (!employeeNoString) {
    logger.warn('hikvision-decrypt', 'No employeeNoString in event', { event });
    return null;
  }

  // If it's 32 characters, it's already MD5 (not encrypted)
  if (employeeNoString.length === 32) {
    logger.debug('hikvision-decrypt', 'Hash is already MD5 (not encrypted)');
    return employeeNoString;
  }

  // If it's 96 characters, it's encrypted - decrypt it
  if (isLikelyEncrypted(employeeNoString)) {
    logger.debug('hikvision-decrypt', 'Encrypted hash detected, decrypting...', {
      hashLength: employeeNoString.length,
    });

    const decrypted = decryptHikvisionData(employeeNoString, iv, password);

    if (decrypted) {
      // The decrypted value should be the original MD5 hash (32 chars)
      if (decrypted.length === 32) {
        logger.info('hikvision-decrypt', 'Successfully decrypted person hash', {
          encrypted: employeeNoString.substring(0, 20) + '...',
          decrypted,
        });
        return decrypted;
      } else {
        logger.warn('hikvision-decrypt', 'Decrypted hash has unexpected length', {
          length: decrypted.length,
          expected: 32,
          decrypted,
        });
      }
    }
  }

  logger.warn('hikvision-decrypt', 'Could not decrypt person hash', {
    hashLength: employeeNoString.length,
  });

  return null;
};

/**
 * Test decryption with known values
 *
 * Use this to verify decryption is working correctly with actual device data
 *
 * Example usage:
 * ```
 * testDecryption(
 *   '0cf7d7e254723469af34ca454e6f11dbe18199db851d6d324b96282effaf27051e5b2597047d5f64e4e5c75fa1ff89d5',
 *   '4f99f7a4391a9dc8eee5cce518ba89b4',
 *   'device-password',
 *   'e81b4256bef79cf7d92029b845c698f8'  // Expected MD5 output
 * );
 * ```
 */
export const testDecryption = (
  encrypted: string,
  iv: string,
  password: string,
  expectedMD5?: string
): boolean => {
  logger.info('hikvision-decrypt', 'Testing decryption', {
    encryptedLength: encrypted.length,
    ivLength: iv.length,
    hasExpected: !!expectedMD5,
  });

  const decrypted = decryptHikvisionData(encrypted, iv, password);

  if (!decrypted) {
    logger.error('hikvision-decrypt', 'Test failed: Decryption returned null');
    return false;
  }

  logger.info('hikvision-decrypt', 'Decryption result', {
    decrypted,
    length: decrypted.length,
  });

  if (expectedMD5) {
    const match = decrypted === expectedMD5;
    logger.info('hikvision-decrypt', 'Comparing with expected value', {
      match,
      expected: expectedMD5,
      actual: decrypted,
    });
    return match;
  }

  return true;
};
